import React from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SearchAIAnalyticsContextLocation } from '../../ai-answer-dialog/search-ai-analytics-context-location';
import {
	type NLPSearchWhoResultCollaboratorDataType,
	type NLPSearchWorkResultRecentActivityDataType,
} from '../../ai-answer-dialog/types';
import { SainAction } from '../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';
import { KnowledgeCardActivityRow } from '../common/knowledge-card-activity-row';
import {
	type ActivityColumnConfig,
	KnowledgeCardBody,
	type UserColumnConfig,
} from '../common/knowledge-card-body';
import { KnowledgeCardUserRow } from '../common/knowledge-card-user-row';
import { KnowledgeCardsIntentType } from '../knowledge-cards-intent-type';
import { useTeamsAppLinks } from '../use-teams-app-links';

type KnowledgeCardsBodyProps = {
	currentUserId: string;
	recommendedUsers: NLPSearchWhoResultCollaboratorDataType[];
	recentActivity: NLPSearchWorkResultRecentActivityDataType[];
	profileUrl?: string;
	viewAllUrl?: string;
	teamId?: string;
};

export const TeamCardBody: React.MemoExoticComponent<
	({
		currentUserId,
		recommendedUsers,
		recentActivity,
		profileUrl,
		viewAllUrl,
		teamId,
	}: KnowledgeCardsBodyProps) => React.JSX.Element
> = React.memo(
	({
		currentUserId,
		recommendedUsers,
		recentActivity,
		profileUrl,
		viewAllUrl,
		teamId,
	}: KnowledgeCardsBodyProps): React.JSX.Element => {
		const { commonAttributes } = useAIAnswerContext();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { formatMessage } = useIntl();
		const { viewAllLink, viewAllOnNavigate, profileHref } = useTeamsAppLinks({
			type: KnowledgeCardsIntentType.TEAM,
			currentUserId,
			viewAllUrl,
			cloudId: commonAttributes.cloudId,
			teamId,
		});

		const handleRecentWorkViewAllClick = (e: React.MouseEvent) => {
			const isAuxClick = 'button' in e && e.button === 1;
			const isContextMenu = 'button' in e && e.button === 2;
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...commonAttributes,
					location: 'recent-work-view-all',
					isAuxClick,
					isContextMenu,
				}),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED, commonAttributes });
			if (!isAuxClick && !isContextMenu && fg('platform-adopt-teams-nav-config')) {
				viewAllOnNavigate(e);
			}
		};

		const handleTeamMembersViewAllClick = (e: React.MouseEvent) => {
			const isAuxClick = 'button' in e && e.button === 1;
			const isContextMenu = 'button' in e && e.button === 2;
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...commonAttributes,
					location: 'team-members-view-all',
					isAuxClick,
					isContextMenu,
				}),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.TEAM_MEMBER_CLICKED, commonAttributes });
			if (!isAuxClick && !isContextMenu && fg('platform-adopt-teams-nav-config')) {
				viewAllOnNavigate(e);
			}
		};

		// Determine which columns to render
		const hasRecentActivity = recentActivity.length > 0;
		const hasTeamMembers = recommendedUsers.length > 0;

		const columnsConfig = [
			// Recent Activity Column
			...(hasRecentActivity
				? [
						{
							viewAllLinkOnClick: handleRecentWorkViewAllClick,
							viewAllLinkHref: viewAllLink,
							element: 'recent-work' as const,
							data: recentActivity,
							component: KnowledgeCardActivityRow,
							header: formatMessage(messages.knowledge_cards_recent_work_subheader),
							onClickCallback: (
								position: number,
								shownAfterCardBodyExpanded: boolean,
								opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
							) => {
								if (
									(opts?.isAuxClick || opts?.isContextMenu) &&
									!SAINFeatureControl.isNonPrimaryClickTrackingEnabled()
								) {
									return;
								}
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'recent-work-item',
										position,
										shownAfterCardBodyExpanded,
										isAuxClick: opts?.isAuxClick,
										isContextMenu: opts?.isContextMenu,
									}),
								);
								fireSAINUsageEvent({
									metCriteria: SainAction.RECENT_WORK_CLICKED,
									commonAttributes,
								});
							},
							searchAIAnalyticsContextLocation: SearchAIAnalyticsContextLocation.SAIN_TEAM_CARD,
						} as unknown as ActivityColumnConfig,
					]
				: []),
			// Team Members Column
			...(hasTeamMembers
				? [
						{
							viewAllLinkOnClick: handleTeamMembersViewAllClick,
							viewAllLinkHref: viewAllLink,
							element: 'members' as const,
							data: recommendedUsers,
							component: KnowledgeCardUserRow,
							onClickCallback: (
								position: number,
								shownAfterCardBodyExpanded: boolean,
								opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
							) => {
								if (
									(opts?.isAuxClick || opts?.isContextMenu) &&
									!SAINFeatureControl.isNonPrimaryClickTrackingEnabled()
								) {
									return;
								}
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'user-list-item',
										position,
										shownAfterCardBodyExpanded,
										isAuxClick: opts?.isAuxClick,
										isContextMenu: opts?.isContextMenu,
									}),
								);
								fireSAINUsageEvent({
									metCriteria: SainAction.TEAM_MEMBER_CLICKED,
									commonAttributes,
								});
							},
							header: formatMessage(messages.knowledge_cards_team_members_subheader),
						} as unknown as UserColumnConfig,
					]
				: []),
		];

		return (
			<KnowledgeCardBody
				feature="team-knowledge-card"
				columnsConfig={columnsConfig}
				profileUrl={fg('platform-adopt-teams-nav-config') ? profileHref : profileUrl}
				footerButtonText={formatMessage(messages.knowledge_cards_view_team)}
				footerButtonAction={SainAction.VIEW_TEAM_PAGE_CLICKED}
				testId={'team-knowledge-card-body'}
			/>
		);
	},
);
